import type { ReactElement } from 'react';
import React, { useMemo } from 'react';
import type { UseQueryResult } from '@tanstack/react-query';
import clsx from 'clsx';
import { sortBy } from 'lodash';
import groupBy from 'lodash/groupBy';
import { max, mean, median, min, quantileSeq, std } from 'mathjs';
import { Card, CardBody, CardHeader } from 'reactstrap';
import GenderBadge from 'feature-components/badges/gender-badge/gender-badge';
import LevelBadge from 'feature-components/badges/level-badge/level-badge';
import BoxWhiskerChart from 'feature-components/charts/box-whisker';
import type { BoxWhiskerData } from 'feature-components/charts/box-whisker/types';
import ReportFooter from 'feature-components/report-parts/report-footer';
import { transformToLevelLadder } from 'helpers/ladder.helpers';
import useActiveDataForStartDate from 'hooks/filters/useActiveDataForStartDate';
import useNationalNormsForStartDate from 'hooks/filters/useNationalNormsForStartDate';
import useTestLadderType from 'hooks/reports/useTestLadderType';
import useTestScaleScoreUnit from 'hooks/reports/useTestScaleScoreUnit';
import { useScrollIntoView } from 'hooks/useScrollIntoView';
import { useResponsesReferenceLevelIds } from 'pages/reports/hooks/useUserRelationIds';
import { useArea } from 'services/assist-api/entities/area/areas';
import type { BaseGender } from 'services/assist-api/entities/gender/genders';
import { useLadders } from 'services/assist-api/entities/ladder-data/ladder-datas';
import { useReferenceDataNationalNormsByAreaAndLevels } from 'services/assist-api/entities/reference-data-national-norm/reference-data-national-norms';
import type { BaseReportResponse } from 'services/assist-api/entities/test-instance-response/reports/report-responses';
import { isFinished } from 'services/assist-api/entities/test-instance-response/test-instance-response';
import { useTest } from 'services/assist-api/entities/test/tests';
import { isError, isInitialLoad } from 'utils/check-query-status';
import Skeleton from './skeleton';
import YearLevelSummary from './summary';

type MinimalResponse = {
  id: number;
  scaleScore: number;
  status: BaseReportResponse['status'];
  testInstance: { startDate: string } | undefined;
  testInstanceUser?: {
    level?: API_JSON;
    referenceLevel?: API_JSON;
    gender?: API_JSON;
  };
};

export type FullBoxWhiskerData = BoxWhiskerData & {
  numberOfStudents: number;
  numberOfFinishedResponses: number;
  referenceLevelId: number;
  std: number;
  mean: number;
};

export type Props = {
  isReferenceDataVisible: boolean;
  period: { startDate: string; endDate: string };
  threshold: number;
  testIds: number[];
  areaId: number;
  queryResponses: {
    queries: UseQueryResult<MinimalResponse[]>[];
    data: MinimalResponse[] | undefined;
  };
  genders?: readonly BaseGender[];
};

export default function LevelBoxWhisker({
  isReferenceDataVisible,
  period,
  queryResponses,
  areaId,
  testIds = [],
  genders = [],
  threshold
}: Props): ReactElement {
  const responses = queryResponses.data;
  const test = useTest(testIds?.length === 1 ? testIds[0] : undefined);
  const area = useArea(areaId);

  const scaleScoreUnit = useTestScaleScoreUnit(area.data?.json.identifier);
  const ladderType = useTestLadderType(area.data?.json.identifier);

  const allLadders = useLadders(
    { filters: { type: ladderType, test: test.data?.json?.id } },
    { enabled: ladderType && test.isSuccess }
  );

  const startDate = queryResponses.data
    ? queryResponses.data[0]?.testInstance?.startDate
    : undefined;

  const laddersForStartDate = useActiveDataForStartDate(allLadders.data?.json, startDate);

  const referenceLevelIds = useResponsesReferenceLevelIds(responses);

  const nationalNorms = useReferenceDataNationalNormsByAreaAndLevels(areaId, referenceLevelIds);

  const nationalNormsForStartDate = useNationalNormsForStartDate(
    nationalNorms.data?.json,
    startDate
  );

  const visibleLadders = useMemo(
    () =>
      referenceLevelIds
        .map((id) => transformToLevelLadder(laddersForStartDate.filter((l) => l.level.id === id)))
        .filter((ladder) => ladder?.length)
        .sort((ladderA, ladderB) => (ladderA[0].id > ladderB[0].id ? 1 : -1)),
    [laddersForStartDate, referenceLevelIds]
  );

  const boxWhiskerData = useMemo(() => {
    const yearGroupedUsers =
      genders?.length > 1
        ? groupBy(
            sortBy(responses, [
              'user.level.id',
              'user.referenceLevel.id',
              'user.user.user.gender.id'
            ]),
            ({ testInstanceUser }) =>
              testInstanceUser
                ? `${testInstanceUser?.level?.id} (${testInstanceUser?.referenceLevel?.id}) - ${
                    genders.find((g) => g.id === testInstanceUser?.gender?.id)?.name
                  }`
                : 'anon'
          )
        : groupBy(
            sortBy(responses, ['user.level.id', 'user.referenceLevel.id']),
            ({ testInstanceUser }) =>
              testInstanceUser
                ? `${testInstanceUser?.level?.id} (${testInstanceUser?.referenceLevel?.id})`
                : 'anon'
          );

    return Object.keys(yearGroupedUsers).map((key) => {
      const yearLevelScores = yearGroupedUsers[key].map((response) => response.scaleScore) || [];
      const levelId = yearGroupedUsers[key]?.[0].testInstanceUser?.level?.id || 0;
      const referenceLevelId = yearGroupedUsers[key]?.[0].testInstanceUser?.referenceLevel?.id;
      const genderId = yearGroupedUsers[key]?.[0].testInstanceUser?.gender?.id || 0;

      return {
        id: `${levelId}-${referenceLevelId}-${genderId}`,
        label: (
          <>
            <LevelBadge id={levelId} referenceId={referenceLevelId} isSmall />
            {genders?.length > 0 && <GenderBadge id={genderId} hideLabel isSmall />}
          </>
        ),
        numberOfStudents: yearGroupedUsers[key]?.length,
        numberOfResponses: yearLevelScores?.length,
        numberOfFinishedResponses: yearGroupedUsers[key]?.filter(({ status }) => isFinished(status))
          .length,
        levelId,
        referenceLevelId,
        ladderId: referenceLevelId,
        className:
          genders?.length > 0 ? genders.find((g) => g.id === genderId)?.name.toLowerCase() : '',
        mean: mean(yearLevelScores),
        std: std(...yearLevelScores),
        median: median(yearLevelScores),
        lowerQuartile: quantileSeq(yearLevelScores, 0.25, false),
        upperQuartile: quantileSeq(yearLevelScores, 0.75, false),
        minimum: min(yearLevelScores),
        maximum: max(yearLevelScores)
      } as FullBoxWhiskerData;
    });
  }, [responses, genders]);

  const boxWhiskerReferenceData = useMemo(() => {
    if (!nationalNormsForStartDate) return undefined;
    return boxWhiskerData
      .map(({ referenceLevelId, id }) => {
        if (nationalNormsForStartDate[referenceLevelId]) {
          return {
            ...nationalNormsForStartDate[referenceLevelId],
            id,
            ladderId: referenceLevelId
          };
        }
        return undefined;
      })
      .filter(Boolean) as unknown as FullBoxWhiskerData[];
  }, [boxWhiskerData, nationalNormsForStartDate]);

  const viewRef = useScrollIntoView(
    queryResponses.queries.every((q) => q.isFetched),
    area.isFetched,
    test.isFetched,
    allLadders.isFetched
  );

  /*  Display loading / error skeleton */
  if (
    isInitialLoad(...queryResponses.queries, area, test) ||
    (isReferenceDataVisible && isInitialLoad(nationalNorms))
  ) {
    return <Skeleton isLoading />;
  }

  if (
    isError(...queryResponses.queries, area, test) ||
    (isReferenceDataVisible && isError(nationalNorms))
  ) {
    return <Skeleton isError />;
  }

  return (
    <Card innerRef={viewRef} className={clsx('my-0 ms-0 flex-grow-1', area.data?.json.identifier)}>
      <CardHeader className="d-flex justify-content-between">
        <h3 className="mb-0 card-header-title">{test.data?.json.name || area.data?.json.name}</h3>
      </CardHeader>
      <CardBody>
        <YearLevelSummary
          data={boxWhiskerData}
          referenceData={boxWhiskerReferenceData}
          isNationalVisible={isReferenceDataVisible}
          threshold={threshold}
          className={area.data?.json.identifier}
        />
        {!boxWhiskerData?.length ? (
          <h4 className="text-center">There is insufficient data to generate this report</h4>
        ) : (
          <BoxWhiskerChart
            data={boxWhiskerData}
            referenceData={isReferenceDataVisible ? boxWhiskerReferenceData : []}
            ladders={
              area.data?.json.identifier === 'trm' && visibleLadders[0]
                ? [visibleLadders[0]?.map((l) => ({ ...l, ladderName: l.type }))]
                : visibleLadders
            }
            config={{ height: 700, width: 1000, maxBoxWidth: 200, margin: { bottom: 90 } }}
            className={area.data?.json.identifier}
            scaleUnit={scaleScoreUnit}
            threshold={threshold}
            yLabel="Scale Score"
            xLabel="Year Level"
          />
        )}
      </CardBody>
      <ReportFooter theme={area.data?.json.identifier} period={period} />
    </Card>
  );
}
